import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ArrowLeft, ArrowRight, Loader2, BookOpen, ToggleLeft, ToggleRight, AlertTriangle, Info, Plus } from 'lucide-react';
import { onboardingApi } from '../../services/api';
import type { OnboardingState, SuggestedRule, DomainType } from '../../types';
import AddRuleModal from './AddRuleModal';

interface DomainOption {
  value: DomainType;
  label: string;
  description: string;
}

interface Props {
  state: OnboardingState;
  setRules: (rules: SuggestedRule[]) => void;
  setDomain: (domain: DomainType) => void;
  domains: DomainOption[];
  onNext: () => void;
  onBack: () => void;
}

const confidenceBadge: Record<string, { color: string; icon: string }> = {
  high: { color: 'text-cargo-success bg-cargo-success/10', icon: '●' },
  medium: { color: 'text-cargo-warning bg-cargo-warning/10', icon: '◐' },
  low: { color: 'text-cargo-muted bg-cargo-border/30', icon: '○' },
};

const RulePreview: React.FC<Props> = ({ state, setRules, setDomain, domains, onNext, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addRuleModalOpen, setAddRuleModalOpen] = useState(false);

  // Gather all column names for the column dropdown in AddRuleModal
  const allColumns = Object.values(state.columns).flat().map((c: any) => c.column_name || c.name || '').filter(Boolean);

  const handleManualRulesAdded = (newRules: SuggestedRule[]) => {
    setRules([...state.suggestedRules, ...newRules]);
  };

  const kwargLabels: Record<string, string> = {
    min_value: 'Min',
    max_value: 'Max',
    value_set: 'Values',
    column_set: 'Columns',
    regex: 'Pattern',
    mostly: 'Threshold',
    strftime_format: 'Format',
    column_A: 'Column A',
    column_B: 'Column B',
    type_: 'Type',
    or_equal: 'Allow Equal',
    exact_match: 'Exact Match',
  };

  const formatKwargKey = (key: string): string =>
    kwargLabels[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const formatKwargValue = (key: string, value: unknown): React.ReactNode => {
    if (key === 'mostly' && typeof value === 'number')
      return <span className="font-mono">{Math.round(value * 100)}%</span>;
    if (typeof value === 'boolean')
      return <span>{value ? 'Yes' : 'No'}</span>;
    if (Array.isArray(value)) {
      const shown = value.slice(0, 5).join(', ');
      const rest = value.length > 5 ? ` +${value.length - 5} more` : '';
      return <span className="font-mono">{shown}{rest}</span>;
    }
    if (typeof value === 'string')
      return <span className="font-mono">{value}</span>;
    if (typeof value === 'number')
      return <span className="font-mono">{value.toLocaleString()}</span>;
    return <span className="font-mono">{String(value)}</span>;
  };

  const renderKwargs = (kwargs: Record<string, any> | undefined) => {
    if (!kwargs || Object.keys(kwargs).length === 0) return null;
    return (
      <div className="flex flex-wrap gap-1 mt-1.5">
        {Object.entries(kwargs).map(([key, value]) => (
          <span
            key={key}
            className="inline-flex items-center gap-1 text-[10px] bg-cargo-border/20 px-1.5 py-0.5 rounded"
          >
            <span className="text-cargo-muted">{formatKwargKey(key)}:</span>
            <span className="text-cargo-text">{formatKwargValue(key, value)}</span>
          </span>
        ))}
      </div>
    );
  };

  const generateRules = useCallback(async (domain: DomainType) => {
    setLoading(true);
    setError('');
    try {
      const res = await onboardingApi.autoGenerateRules({
        ...state.connection,
        schema: state.selectedSchema,
        tables: state.selectedTables.map((t) => t.table_name),
        domain,
        // Reuse the Profile step's result so the backend doesn't
        // re-pull + re-profile (falls back server-side if absent).
        profile: state.profileResult,
      });
      const rules: SuggestedRule[] = (res.data.payload || []).map((r: any) => ({
        ...r,
        enabled: r.confidence !== 'low',
      }));
      setRules(rules);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate rules');
    } finally {
      setLoading(false);
    }
  }, [state.connection, state.selectedSchema, state.selectedTables, state.profileResult, setRules]);

  /* Auto-generate on mount (ref guard prevents StrictMode double-fire) */
  const rulesFetched = useRef(false);
  useEffect(() => {
    if (rulesFetched.current) return;
    if (state.suggestedRules.length === 0) {
      rulesFetched.current = true;
      generateRules(state.domain);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDomainChange = (newDomain: DomainType) => {
    setDomain(newDomain);
    generateRules(newDomain);
  };

  const toggleRule = (idx: number) => {
    const updated = [...state.suggestedRules];
    updated[idx] = { ...updated[idx], enabled: !updated[idx].enabled };
    setRules(updated);
  };

  const enabledCount = state.suggestedRules.filter((r) => r.enabled).length;
  const highCount = state.suggestedRules.filter((r) => r.confidence === 'high').length;
  const medCount = state.suggestedRules.filter((r) => r.confidence === 'medium').length;
  const lowCount = state.suggestedRules.filter((r) => r.confidence === 'low').length;

  /* Group rules by table → column */
  const grouped = state.suggestedRules.reduce<Record<string, { rule: SuggestedRule; idx: number }[]>>((acc, rule, i) => {
    const key = rule.table_name
      ? `${rule.table_name} → ${rule.column_name || 'table-level'}`
      : rule.column_name || '_table_level';
    if (!acc[key]) acc[key] = [];
    acc[key].push({ rule, idx: i });
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-cargo-accent animate-spin mb-4" />
        <p className="text-cargo-muted">Profiling data and generating rules with CARGO engine...</p>
        <p className="text-cargo-muted/60 text-xs mt-2">
          Domain: <strong className="text-cargo-text">{state.domain}</strong> — Analyzing distributions, patterns, cardinality, nulls...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-cargo-surface border border-cargo-error/30 rounded-xl p-6 text-center">
        <AlertTriangle className="w-8 h-8 text-cargo-error mx-auto mb-3" />
        <p className="text-cargo-error mb-4">{error}</p>
        <button onClick={onBack} className="text-cargo-accent hover:underline text-sm">
          Go back and try again
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Domain selector + stats */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-cargo-text">CARGO Suggested Rules</h2>
          <button
            onClick={() => setAddRuleModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-cargo-accent/15 text-cargo-accent
                       text-xs font-medium rounded-lg hover:bg-cargo-accent/25 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Rules Manually
          </button>
        </div>

        <div className="flex items-center gap-3">
          {/* Domain dropdown */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-cargo-muted">Industry:</label>
            <select
              value={state.domain}
              onChange={(e) => handleDomainChange(e.target.value as DomainType)}
              className="bg-cargo-panel border border-cargo-border text-cargo-text text-xs
                         rounded-lg px-2.5 py-1.5 focus:border-cargo-accent focus:ring-1
                         focus:ring-cargo-accent outline-none cursor-pointer"
            >
              {domains.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>

          {/* Confidence stats */}
          <div className="flex items-center gap-2 text-xs border-l border-cargo-border pl-3">
            <span className="text-cargo-success">● {highCount}</span>
            <span className="text-cargo-warning">◐ {medCount}</span>
            <span className="text-cargo-muted">○ {lowCount}</span>
            <span className="text-cargo-border">|</span>
            <span className="text-cargo-text font-medium">{enabledCount}/{state.suggestedRules.length}</span>
          </div>
        </div>
      </div>

      {/* Domain description hint */}
      {state.domain !== 'default' && (
        <div className="mb-4 px-3 py-2 bg-cargo-accent/5 border border-cargo-accent/20 rounded-lg text-xs text-cargo-accent flex items-center gap-2">
          <Info className="w-3.5 h-3.5 flex-shrink-0" />
          {domains.find((d) => d.value === state.domain)?.description} — domain-specific rules added for matching columns.
        </div>
      )}

      <div className="space-y-4 max-h-[26rem] overflow-y-auto pr-1">
        {Object.entries(grouped).map(([groupKey, items]) => (
          <div
            key={groupKey}
            className="bg-cargo-surface border border-cargo-border rounded-xl p-4"
          >
            <h3 className="text-sm font-semibold text-cargo-accent mb-3 flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              {groupKey === '_table_level' ? 'Table-level rules' : groupKey}
              <span className="text-cargo-muted font-normal text-xs">({items.length})</span>
            </h3>
            <div className="space-y-2">
              {items.map(({ rule, idx }) => {
                const original = state.suggestedRules[idx];
                const conf = rule.confidence || 'low';
                const badge = confidenceBadge[conf] || confidenceBadge.low;
                const reason = rule.reason || '';
                const dimension = rule.category || '';
                const source = rule.source || 'profiler';
                return (
                  <div
                    key={idx}
                    className={`flex items-start justify-between px-3 py-2.5 rounded-lg transition-colors ${
                      original.enabled ? 'bg-cargo-panel' : 'bg-cargo-panel/50 opacity-50'
                    }`}
                  >
                    <div className="flex-1 min-w-0 mr-3">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${badge.color}`}>
                          {badge.icon} {conf}
                        </span>
                        {dimension && (
                          <span className="text-xs text-cargo-muted/70 capitalize">{dimension}</span>
                        )}
                        {source === 'domain' && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400">
                            domain
                          </span>
                        )}
                        {source === 'domain+profiler' && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-green-500/10 text-green-400">
                            domain confirmed
                          </span>
                        )}
                        {!original.auto_suggested && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400">
                            manual
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-cargo-text">{rule.display_name}</p>
                      <p className="text-xs text-cargo-muted font-mono truncate">{rule.rule_name}</p>
                      {reason && (
                        <p className="text-xs text-cargo-muted/60 mt-1 flex items-start gap-1">
                          <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          {reason}
                        </p>
                      )}
                      {renderKwargs(rule.kwargs)}
                    </div>
                    <button onClick={() => toggleRule(idx)} className="flex-shrink-0 mt-1">
                      {original.enabled ? (
                        <ToggleRight className="w-6 h-6 text-cargo-success" />
                      ) : (
                        <ToggleLeft className="w-6 h-6 text-cargo-muted" />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {state.suggestedRules.length === 0 && (
          <p className="text-cargo-muted text-center py-8">
            No rules could be generated. Go back and check your table selection.
          </p>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-cargo-muted hover:text-cargo-text transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={onNext}
          disabled={enabledCount === 0}
          className="flex items-center gap-2 bg-cargo-accent hover:bg-cargo-accent-hover text-white
                     font-medium py-2.5 px-5 rounded-lg transition-colors disabled:opacity-30"
        >
          Next <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Add Rule from Catalog modal */}
      <AddRuleModal
        open={addRuleModalOpen}
        onClose={() => setAddRuleModalOpen(false)}
        onAddRules={handleManualRulesAdded}
        columns={allColumns}
        tableName={state.selectedTables[0]?.table_name || ''}
      />
    </div>
  );
};

export default RulePreview;
